import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { detailOf } from './http-trade-read.service'

/**
 * The flex-transactions pass-through for RPS's GL journal-entry read (helios PVAL-26693).
 *
 * With `<base href="/helios/">` this resolves to
 * `/helios/helios-flex-transactions/transactions/gl/journalentries/get` — the sibling of the trade
 * read, on the same host and so the same LOCAL-ONLY `proxy.conf.beta.json` entry.
 */
export const GL_ENTRY_GET_URL = `helios-flex-transactions/transactions/gl/journalentries/get`

/**
 * The request body `POST api/transactions/gl/journalentries/get` binds (helios
 * `GlJournalEntryGetApiRequest`).
 *
 * `ids` are GL entry ids, sent as numbers: helios refuses the WHOLE call with a 400 for an empty list
 * or for any id that is not positive. There is no `clientId`: helios derives the client from
 * [accountId], which must be an account of the caller's session (`helios-session-id`, attached by the
 * app's `SessionInterceptor`), otherwise it answers a 400 `accountNotInSession`.
 */
export interface GlEntryGetRequest {
  accountId: number
  ids: number[]
}

/** RPS relays Enfusion's document verbatim, so nothing here models its shape. */
export type GlEntryDocument = Record<string, unknown>

/** Requested entry id → the journal entry Enfusion answered for it. Absent means it resolved to nothing. */
export type GlEntriesById = ReadonlyMap<string, GlEntryDocument>

/**
 * What a read answered: the entries, and the failure if there was one.
 *
 * The error is RETURNED, not thrown, for the reason the trade read gives: a failed read must not take
 * the dialog down with it, but it must not vanish either.
 */
export interface GlEntryReadResult {
  entries: GlEntriesById
  error: unknown | null
}

/** A whole positive number — the only kind of id helios accepts. */
const POSITIVE_ID = /^[1-9]\d*$/

/**
 * Reads whole GL journal entries from Enfusion, through helios, to prefill the GL Entry dialog.
 *
 * The same job as `HttpTradeReadService`, for the record a GL row stands for: the grid row carries
 * only the columns the workbench asked for, and the dialog needs every key the metadata declares,
 * distribution lines included. One call serves a selection of any size; the single dialog is the
 * one-element case.
 */
@Injectable()
export class HttpGlEntryReadService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Entries for every id asked for, keyed by the id as it was REQUESTED, read for the client that
   * owns [accountId] — the raw account id the dialog is open on.
   *
   * Never throws and never fails a dialog: a rejected or empty read answers an empty map, leaving
   * every row on the values it already had. An id that is not a whole positive number is left out
   * rather than sent, because helios would refuse the whole call for it.
   */
  loadMany(
    entryIds: readonly string[],
    accountId: string,
  ): Observable<GlEntryReadResult> {
    const asked = [...new Set(entryIds.map(id => id.trim()).filter(Boolean))]
    const ids = asked.filter(id => POSITIVE_ID.test(id))
    if (ids.length < asked.length) {
      console.warn(
        `[transactions-2] ${
          asked.length - ids.length
        } GL entry id(s) are not whole positive numbers and were not read`,
        asked.filter(id => !POSITIVE_ID.test(id)),
      )
    }
    if (!ids.length) return of({ entries: EMPTY_ENTRIES, error: null })

    const body: GlEntryGetRequest = {
      accountId: Number(accountId),
      ids: ids.map(Number),
    }

    return this.http
      .post<unknown>(GL_ENTRY_GET_URL, body, {
        // Without this the app's global `ErrorInterceptor` returns EMPTY on every HTTP error, the
        // observable completes without emitting, and a dialog waiting on the read waits forever.
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      })
      .pipe(
        map(document => readResult(document, ids)),
        catchError((error: unknown) => {
          // 400 = helios refused it (no accountId, no ids, an account outside the session:
          // `accountNotInSession`) or RPS rejected it; 401 = recon:Access; anything else is a 500.
          console.error(
            `[transactions-2] GL entry read failed for ${ids.length} id(s); the dialog keeps its ` +
              `grid-projected values`,
            error,
          )
          return of({
            entries: EMPTY_ENTRIES,
            error: detailOf(error, GL_ENTRY_GET_URL),
          })
        }),
      )
  }
}

const EMPTY_ENTRIES: GlEntriesById = new Map<string, GlEntryDocument>()

/** An object or an array. `typeof` alone lets `null` through, which narrows nothing out. */
const isObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object'

/**
 * A successful response that is not a document is a FAILURE, not an empty read — most often the dev
 * proxy falling through to the SPA's `index.html`, which arrives as HTML with a 200 and would
 * otherwise read as "no entries matched", with no error and no symptom at all.
 */
const readResult = (
  document: unknown,
  ids: readonly string[],
): GlEntryReadResult => {
  if (!isObject(document)) {
    console.error(
      '[transactions-2] GL entry read answered something that is not a document; treating it as a ' +
        'failure. A proxy falling through to index.html looks exactly like this.',
      document,
    )
    return {
      entries: EMPTY_ENTRIES,
      error: {
        status: 200,
        statusText: 'OK',
        url: GL_ENTRY_GET_URL,
        message:
          'The GL entry read answered a 200 that is not a document. Check the endpoint is routed ' +
          'and not being served the SPA shell.',
        body: typeof document === 'string' ? document.slice(0, 500) : document,
      },
    }
  }
  const entries = glEntriesById(document, ids)
  if (!entries.size) {
    console.warn(
      '[transactions-2] GL entry read answered no entry for the ids asked for; the dialog keeps its ' +
        'grid-projected values',
      {
        asked: ids,
        keys: Array.isArray(document) ? 'array' : Object.keys(document),
      },
    )
  }
  return { entries, error: null }
}

/**
 * The key a returned entry carries its own id under: `id`, as a trade does. The request keys on
 * `ids` and the entry answers `id`.
 */
const ENTRY_ID_KEY = 'id'

/**
 * The answered entries, whichever way the document holds them: RPS relays `{entries: [...]}`, and a
 * root array or a lone entry is tolerated rather than read as "nothing came back".
 */
const entriesOf = (document: unknown): unknown[] => {
  if (Array.isArray(document)) return document
  if (!isObject(document)) return []
  const { entries } = document as { entries?: unknown }
  if (Array.isArray(entries)) return entries
  return ENTRY_ID_KEY in document ? [document] : []
}

/**
 * The answered entries, indexed by the id each was REQUESTED under.
 *
 * **Matched by id, never taken positionally.** Nothing promises the answer is ordered like the
 * request or the same length — an id that resolves to nothing is simply absent — so indexing by
 * position would put one entry's values into another's dialog on any such gap. The one-id,
 * one-entry case still pairs up whatever the id's spelling.
 */
export const glEntriesById = (
  document: unknown,
  requested: readonly string[],
): GlEntriesById => {
  const entries = entriesOf(document).filter(
    (entry): entry is GlEntryDocument =>
      entry != null && typeof entry === 'object' && !Array.isArray(entry),
  )

  const found = new Map<string, GlEntryDocument>()
  for (const id of requested) {
    const match = entries.find(
      entry => String(entry[ENTRY_ID_KEY] ?? '').trim() === id,
    )
    if (match) found.set(id, match)
  }

  if (!found.size && requested.length === 1 && entries.length === 1) {
    found.set(requested[0], entries[0])
  }
  return found
}
